import { useEffect, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import {
  Box,
  Container,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import { LoginWindow } from "../../components/LoginWindow";
import {
  getChurch,
  listBaptism,
  listChurch,
  updateChurch,
} from "../../utils/client";
import {
  downloadCSV,
  formatAddress,
  handleSort,
  humaneReadableShortTime,
  isAdmin,
  isoTimestamp,
} from "../../utils/helper";
import { useUserData } from "../../components/DataProvider";
import TopNavBar from "../../components/TopNavBar";
import { Baptism, ValidationError } from "baptize-america-client";
import EditableCell from "../../components/EditableCell";
import { useAlert } from "../../components/AlertProvider";
import { Download } from "@phosphor-icons/react";
import DebouncedButton from "../../components/DebounceButton";

const BaptizeAmericaOverview = () => {
  interface Church {
    readonly churchId: string;
    readonly baptismCount: number;
    readonly name: string;
    readonly location: string;
    readonly baptismPerformed: number;
    readonly salvationCount: number;
    readonly baptisms:
      | [
          {
            readonly baptismId: string;
            readonly time: string;
            readonly note: string;
          },
        ]
      | undefined;
  }

  interface Row {
    readonly churchId: string;
    readonly showChurchInfo: boolean;
    readonly rowSpan: number;
    readonly baptismCount: number;
    readonly name: string;
    readonly location: string;
    readonly baptismPerformed: number;
    readonly salvationCount: number;
    readonly baptismId: string;
    readonly time: string;
    readonly note: string;
  }

  const { setAlert } = useAlert();
  const theme = useTheme();
  const [orderBy, setOrderBy] = useState<keyof Church>("name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [churches, setChurches] = useState<Church[]>([]);
  const [compactMode, setCompactMode] = useState<boolean>(false);
  const { authStatus } = useAuthenticator((context) => [context.user]);
  const { attributes } = useUserData();
  const [editingBaptismRowId, setEditingBaptismRowId] = useState<string | null>(
    null,
  );
  const [editingSalvationRowId, setEditingSalvationRowId] = useState<
    string | null
  >(null);
  const styles = {
    navBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexDirection: "column",
      mb: 5,
    },
  };

  useEffect(() => {
    const getData = async () => {
      const churches = await listChurch({});
      //Get all baptisms
      let baptisms: Baptism[] = [];
      let nextToken = undefined;
      do {
        const subset = await listBaptism({ nextToken });
        nextToken = subset.nextToken;
        baptisms = [...baptisms, ...(subset.baptisms ?? [])];
      } while (nextToken !== undefined);

      // Group baptisms by churchId
      const churchIdToBaptisms = new Map<string, Baptism[]>();
      for (const baptism of baptisms) {
        if (!baptism.churchId) continue;
        if (!churchIdToBaptisms.has(baptism.churchId)) {
          churchIdToBaptisms.set(baptism.churchId, []);
        }
        churchIdToBaptisms.get(baptism.churchId)!.push(baptism);
      }

      const result: Church[] = (churches.churches ?? []).map((church) => {
        const baptismCount =
          churchIdToBaptisms.get(church.churchId ?? "")?.length ?? 0;
        return {
          churchId: church.churchId,
          baptismCount: baptismCount,
          name: church.name,
          location: `${church.address?.city ?? ""}, ${church.address?.state ?? ""}`,
          baptismPerformed: church.reported?.baptismPerformed ?? 0,
          salvationCount: church.reported?.salvationCount ?? 0,
          baptisms: churchIdToBaptisms
            .get(church.churchId ?? "")
            ?.map((baptism) => ({
              baptismId: baptism.baptismId,
              time:
                baptism.time && baptism.timezone
                  ? humaneReadableShortTime(baptism.time!!, baptism.timezone!!)
                  : "",
              note: baptism.note,
            })),
        } as Church;
      });

      setChurches(result);
    };

    getData();
  }, []);

  function flattenChurchesToRows(churches: Church[]): Row[] {
    return churches.flatMap((church) =>
      (church.baptisms ?? [{ baptismId: "", time: "", note: "" }]).map(
        (baptism, idx) => ({
          churchId: church.churchId,
          showChurchInfo: idx === 0,
          rowSpan: church.baptismCount === 0 ? 1 : church.baptismCount,
          baptismCount: church.baptismCount,
          name: church.name,
          location: church.location,
          baptismPerformed: church.baptismPerformed,
          salvationCount: church.salvationCount,
          baptismId: baptism.baptismId,
          time: baptism.time,
          note: baptism.note,
        }),
      ),
    );
  }

  const updateReportedStats = async ({
    churchId,
    salvations,
    baptisms,
  }: {
    churchId: string;
    salvations: number | null;
    baptisms: number | null;
  }) => {
    try {
      const church = await getChurch({ churchId: churchId });
      await updateChurch({
        churchId: church.churchId,
        name: church.name,
        address: church.address,
        websiteUrl: church.websiteUrl,
        instagramUrl: church.instagramUrl,
        reported: {
          salvationCount:
            salvations === null ? church.reported?.salvationCount : salvations,
          baptismPerformed:
            baptisms === null ? church.reported?.baptismPerformed : baptisms,
        },
        leadPastor: church.leadPastor,
      });
      setAlert({
        showAlert: true,
        alertMsg: "Updated!",
        alertType: "success",
      });
    } catch (err) {
      console.log("Error loading dashboard", err);
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error Loading Dashboard",
        alertType: "error",
      });
    }
  };

  const SortableTableCell = ({
    label,
    columnKey,
  }: {
    label: string;
    columnKey: keyof Church;
  }) => (
    <TableCell sortDirection={orderBy === "baptismCount" ? order : false}>
      <TableSortLabel
        active={orderBy === columnKey}
        direction={orderBy === columnKey ? order : "asc"}
        onClick={() =>
          handleSort<Church>(
            columnKey,
            orderBy,
            order,
            setOrder,
            setOrderBy,
            setChurches,
          )
        }
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return authStatus === "authenticated" && isAdmin(attributes) ? (
    <Container>
      <Box
        sx={[
          {
            "& .MuiTextField-root": {
              m: 1,
              width: theme.breakpoints.values.sm,
            },
          },
          styles.navBar,
        ]}
      >
        <Box sx={{ mb: 4 }}>
          <TopNavBar showBackArrow={true} />
        </Box>
        <Stack direction={"row"} mb={1}>
          <FormControlLabel
            control={
              <Switch
                checked={compactMode}
                onChange={(e) => setCompactMode(e.target.checked)}
              />
            }
            label="Compact Mode"
          />
          <DebouncedButton
            startIcon={<Download weight={"fill"} />}
            onClick={async () => {
              const churches = await listChurch({});

              interface FlatChurch {
                churchId: string;
                name: string;
                streetAddress1: string;
                streetAddress2: string;
                city: string;
                state: string;
                postalCode: string;
                country: string;
                websiteUrl: string;
                instagramUrl: string;
                baptismPerformed: string;
                salvationCount: string;
                leadPastorFirstName: string;
                leadPastorLastName: string;
              }

              const flatChurches: FlatChurch[] = (churches.churches ?? []).map(
                (it) => ({
                  churchId: it.churchId ?? "",
                  name: it.name ?? "",
                  streetAddress1: it.address?.streetAddress1 ?? "",
                  streetAddress2: it.address?.streetAddress2 ?? "",
                  city: it.address?.city ?? "",
                  state: it.address?.state ?? "",
                  postalCode: it.address?.postalCode ?? "",
                  country: it.address?.country ?? "",
                  address:
                    it.address !== undefined ? formatAddress(it.address) : "",
                  websiteUrl: it.websiteUrl ?? "",
                  instagramUrl: it.instagramUrl ?? "",
                  baptismPerformed: `${it.reported?.baptismPerformed ?? "0"}`,
                  salvationCount: `${it.reported?.salvationCount ?? "0"}`,
                  leadPastorFirstName: it.leadPastor?.firstName ?? "",
                  leadPastorLastName: it.leadPastor?.lastName ?? "",
                }),
              );
              downloadCSV(flatChurches ?? [], `Overview-${isoTimestamp()}`);
            }}
          >
            Export to CSV
          </DebouncedButton>
        </Stack>
        <TableContainer sx={{ width: "95vw" }} component={Paper}>
          <Table size="small" stickyHeader={true}>
            <TableHead>
              <TableRow>
                <SortableTableCell
                  columnKey="baptismCount"
                  label="Baptisms Events"
                />
                <SortableTableCell columnKey="name" label="Church" />
                <SortableTableCell columnKey="location" label="Location" />
                <SortableTableCell
                  columnKey="baptismPerformed"
                  label="Reported Baptisms"
                />
                <SortableTableCell
                  columnKey="salvationCount"
                  label="Reported Salvation's"
                />
                <TableCell>Baptism Time</TableCell>
                {!compactMode && <TableCell>Baptism Note</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {flattenChurchesToRows(churches).map((row) => (
                <TableRow
                  key={row.baptismId + row.churchId}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  {row.showChurchInfo && (
                    <TableCell rowSpan={row.rowSpan}>
                      {row.baptismCount}
                    </TableCell>
                  )}
                  {row.showChurchInfo && (
                    <TableCell rowSpan={row.rowSpan}>
                      <Link href={`/churches/${row.churchId}`}>{row.name}</Link>
                    </TableCell>
                  )}
                  {row.showChurchInfo && (
                    <TableCell rowSpan={row.rowSpan}>{row.location}</TableCell>
                  )}
                  {row.showChurchInfo && (
                    <EditableCell
                      rowSpan={row.rowSpan}
                      churchId={row.churchId}
                      value={row.baptismPerformed}
                      isEditing={editingBaptismRowId === row.churchId}
                      onStartEdit={() => {
                        setEditingBaptismRowId(row.churchId);
                      }}
                      onSave={async (count) => {
                        setChurches((prev) =>
                          prev.map((r) =>
                            r.churchId === row.churchId
                              ? { ...r, baptismPerformed: count }
                              : r,
                          ),
                        );
                        setEditingBaptismRowId(null);
                        await updateReportedStats({
                          churchId: row.churchId,
                          baptisms: count,
                          salvations: null,
                        });
                      }}
                      onCancel={() => setEditingBaptismRowId(null)}
                    />
                  )}
                  {row.showChurchInfo && (
                    <EditableCell
                      rowSpan={row.rowSpan}
                      churchId={row.churchId}
                      value={row.salvationCount}
                      isEditing={editingSalvationRowId === row.churchId}
                      onStartEdit={() => {
                        setEditingSalvationRowId(row.churchId);
                      }}
                      onSave={async (count) => {
                        setChurches((prev) =>
                          prev.map((r) =>
                            r.churchId === row.churchId
                              ? { ...r, salvationCount: count }
                              : r,
                          ),
                        );
                        setEditingSalvationRowId(null);
                        await updateReportedStats({
                          churchId: row.churchId,
                          baptisms: null,
                          salvations: count,
                        });
                      }}
                      onCancel={() => setEditingSalvationRowId(null)}
                    />
                  )}
                  <TableCell width="150">{row.time}</TableCell>
                  {!compactMode && <TableCell>{row.note}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  ) : (
    <LoginWindow />
  );
};
export default BaptizeAmericaOverview;
